import { useEffect, useState } from "react";
import {
  AppBreadcrumbs,
  AppButton,
  EmptyState,
  PageHeader,
  StatusChip,
  toast,
} from "../../../../components/ui";
import {
  Avatar,
  Box,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import { useNavigate, useParams } from "react-router-dom";
import {
  ApplicationRole,
  ManagedUserResponse,
  UserViewModel,
  UsersService,
} from "../../../../api";
import { isUserActive, toggleUserActive } from "../shared/userActiveActions";
import ResetPasswordDialog from "../shared/ResetPasswordDialog";
import { formatRole } from "../shared/roleLabels";
import UserProfileForm from "./UserProfileForm";

const surfaceSx = {
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(27, 30, 51, 0.04)",
} as const;

function getInitials(user: UserViewModel) {
  const first = user.firstName?.[0] ?? "";
  const last = user.lastName?.[0] ?? "";
  return `${first}${last}`.toUpperCase() || "?";
}

function isAttorneyUser(user: UserViewModel) {
  return (
    user.roles?.includes(ApplicationRole.ATTORNEY) ||
    user.roles?.includes(ApplicationRole.USER) ||
    false
  );
}

function UserDetailsSkeleton() {
  return (
    <Box sx={{ width: "100%" }}>
      <Skeleton width={200} height={18} sx={{ mb: 1.5 }} />
      <Skeleton width={320} height={36} sx={{ mb: 1 }} />
      <Skeleton width={420} height={22} sx={{ mb: 3 }} />
      <Skeleton variant="rounded" height={280} />
    </Box>
  );
}

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserViewModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);

  const loadUser = () => {
    if (!id) return;
    setLoading(true);
    UsersService.getApiUsers(+id)
      .then(setUser)
      .catch(() => {
        setUser(null);
        toast.error("Could not load user.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleToggleActive = async () => {
    if (!user?.id) return;

    const activating = !isUserActive(user);
    await toggleUserActive(user.id, {
      firstName: user.firstName,
      lastName: user.lastName,
      isActive: activating,
      onSuccess: () => {
        setUser((prev) => (prev ? { ...prev, isActive: activating } : prev));
      },
    });
  };

  if (loading) {
    return <UserDetailsSkeleton />;
  }

  if (!user) {
    return (
      <Box sx={{ width: "100%" }}>
        <AppBreadcrumbs
          items={[
            { label: "Users", to: "/staff-users" },
            { label: "User not found" },
          ]}
        />
        <PageHeader title="User not found" />
        <Box sx={surfaceSx}>
          <EmptyState
            title="User not found"
            description="This account may have been removed, or you may not have access."
            action={
              <AppButton
                startIcon={<ArrowBackIcon />}
                variant="contained"
                onClick={() => navigate("/staff-users")}
              >
                Back to users
              </AppButton>
            }
          />
        </Box>
      </Box>
    );
  }

  const fullName =
    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "User";
  const active = isUserActive(user);
  const attorney = isAttorneyUser(user);
  const roleLabels = (user.roles ?? []).map((role) => formatRole(role));
  const resetPasswordUser: ManagedUserResponse = {
    id: user.id!,
    email: user.email ?? "",
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    isActive: active,
    roles: user.roles,
  };

  return (
    <Box sx={{ width: "100%", pb: 3 }}>
      <AppBreadcrumbs
        items={[
          { label: "Users", to: "/staff-users" },
          { label: fullName },
        ]}
      />

      <PageHeader
        title={
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ minWidth: 0 }}
          >
            <Avatar
              src={user.photo?.url ?? undefined}
              alt={fullName}
              sx={{
                width: 48,
                height: 48,
                fontSize: 18,
                fontWeight: 700,
                bgcolor: "primary.main",
                flexShrink: 0,
              }}
            >
              {getInitials(user)}
            </Avatar>
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                letterSpacing: "-0.02em",
                lineHeight: 1.25,
              }}
            >
              {fullName}
            </Box>
            {active ? (
              <StatusChip size="small" label="Active" tone="dirtyGreen" />
            ) : (
              <StatusChip size="small" label="Inactive" variant="outlined" />
            )}
            {user.isPending && (
              <StatusChip size="small" label="Pending" tone="secondary" />
            )}
            {roleLabels.map((label) => (
              <StatusChip
                key={label}
                size="small"
                label={label}
                tone="secondary"
              />
            ))}
          </Stack>
        }
        actions={
          <>
            {!isEdit && (
              <AppButton
                variant="contained"
                color="primary"
                startIcon={<EditOutlinedIcon />}
                onClick={() => setIsEdit(true)}
              >
                Edit profile
              </AppButton>
            )}
            <AppButton
              variant="outlined"
              color="inherit"
              startIcon={<LockResetIcon />}
              onClick={() => setResetPasswordOpen(true)}
            >
              Reset password
            </AppButton>
            <AppButton
              variant="outlined"
              color={active ? "error" : "primary"}
              startIcon={
                active ? <PersonOffOutlinedIcon /> : <HowToRegOutlinedIcon />
              }
              onClick={handleToggleActive}
            >
              {active ? "Deactivate" : "Reactivate"}
            </AppButton>
            {attorney && user.id && (
              <AppButton
                variant="outlined"
                color="inherit"
                startIcon={<GavelOutlinedIcon />}
                onClick={() => navigate(`/attorney/${user.id}`)}
              >
                Attorney profile
              </AppButton>
            )}
          </>
        }
      />

      <Box sx={surfaceSx}>
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: 1.5,
            bgcolor: "#fafbfd",
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Profile details
            </Typography>
          </Box>
          {isEdit && (
            <StatusChip size="small" label="Editing" tone="primary" />
          )}
        </Box>

        <Box sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 2, md: 2.5 }, minWidth: 0 }}>
          {user.email ? (
            <UserProfileForm
              user={user}
              isEdit={isEdit}
              isAttorney={attorney}
              onCancelEdit={() => setIsEdit(false)}
              onSave={() => {
                setIsEdit(false);
                loadUser();
              }}
            />
          ) : (
            <Typography color="text.secondary">
              This user has no email on file, so the profile form cannot be
              shown.
            </Typography>
          )}
        </Box>
      </Box>

      <ResetPasswordDialog
        open={resetPasswordOpen}
        user={resetPasswordUser}
        onClose={() => setResetPasswordOpen(false)}
      />
    </Box>
  );
}
